import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import {
  Users, Plus, Trash2, Edit2, Search, X, Save, UserPlus,
  Loader2, AlertCircle, CheckCircle2, Info, UserCog, ChevronDown, ChevronUp
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const TeamConfigView = ({ onBack, currentUser }) => {
  // State for teams
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState({});
  
  // State for ClickUp users
  const [clickupUsers, setClickupUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  
  // State for system users (for manager assignment)
  const [systemUsers, setSystemUsers] = useState([]);
  const [systemUsersLoading, setSystemUsersLoading] = useState(false);
  
  // State for team creation/editing
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamName, setTeamName] = useState("");
  const [teamDescription, setTeamDescription] = useState("");
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  
  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Load teams on mount
  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    setTeamsLoading(true);
    try {
      const response = await axios.get(`${API}/api/teams`, { withCredentials: true });
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error("Error loading teams:", error);
    } finally {
      setTeamsLoading(false);
    }
  };

  const loadClickupUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const response = await axios.get(`${API}/api/teams/clickup-users`, { withCredentials: true });
      setClickupUsers(response.data.users || []);
    } catch (error) {
      console.error("Error loading ClickUp users:", error);
      setUsersError(error.response?.data?.detail || "Error al cargar usuarios de ClickUp");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadSystemUsers = async () => {
    setSystemUsersLoading(true);
    try {
      const response = await axios.get(`${API}/api/teams/system-users`, { withCredentials: true });
      setSystemUsers(response.data.users || []);
    } catch (error) {
      // Si no es admin, el endpoint retornará error - eso es OK, simplemente no mostramos el campo
      console.log("Could not load system users (may not be admin):", error.response?.status);
      setSystemUsers([]);
    } finally {
      setSystemUsersLoading(false);
    }
  };

  // Filter users by search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return clickupUsers;
    const query = searchQuery.toLowerCase();
    return clickupUsers.filter(user => 
      user.username?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query)
    );
  }, [clickupUsers, searchQuery]);

  const toggleTeamExpanded = (teamId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [teamId]: !prev[teamId]
    }));
  };

  const openCreateDialog = () => {
    setEditingTeam(null);
    setTeamName("");
    setTeamDescription("");
    setSelectedMembers([]);
    setSelectedManager(null);
    setSearchQuery("");
    setIsDialogOpen(true);
    loadClickupUsers();
    loadSystemUsers(); // Always load system users for manager selection
  };

  const openEditDialog = (team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setTeamDescription(team.description || "");
    setSelectedMembers(team.members || []);
    setSelectedManager(team.manager || null);
    setSearchQuery("");
    setIsDialogOpen(true);
    loadClickupUsers();
    loadSystemUsers(); // Always load system users for manager selection
  };

  const handleMemberToggle = (user) => {
    const exists = selectedMembers.some(m => m.user_id === user.user_id);
    if (exists) {
      setSelectedMembers(selectedMembers.filter(m => m.user_id !== user.user_id));
    } else {
      setSelectedMembers([...selectedMembers, {
        user_id: user.user_id,
        username: user.username,
        email: user.email,
        specialty: null
      }]);
    }
  };

  const handleManagerChange = (userId) => {
    if (userId === "none") {
      setSelectedManager(null);
    } else {
      const user = systemUsers.find(u => u.user_id === userId);
      if (user) {
        setSelectedManager({
          user_id: user.user_id,
          email: user.email,
          name: user.name || user.email
        });
      }
    }
  };

  const handleSaveTeam = async () => {
    if (!teamName.trim()) return;
    if (selectedMembers.length === 0) return;
    
    setSaving(true);
    try {
      const payload = {
        name: teamName.trim(),
        description: teamDescription.trim(),
        members: selectedMembers,
        manager: selectedManager
      };

      if (editingTeam) {
        await axios.put(`${API}/api/teams/${editingTeam.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/teams`, payload, { withCredentials: true });
      }
      
      setIsDialogOpen(false);
      loadTeams();
    } catch (error) {
      console.error("Error saving team:", error);
      alert(error.response?.data?.detail || "Error al guardar el equipo");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/teams/${teamId}`, { withCredentials: true });
      setDeleteConfirm(null);
      loadTeams();
    } catch (error) {
      console.error("Error deleting team:", error);
      alert(error.response?.data?.detail || "Error al eliminar el equipo");
    } finally {
      setDeleting(false);
    }
  };

  const canEditTeam = (team) => {
    // Always show edit button - backend will validate permissions
    // This ensures the UI is usable even without currentUser info
    return true;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={onBack} className="gap-2">
              <X className="h-4 w-4" />
              Cerrar configuración
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="h-6 w-6 text-indigo-600" />
                Configuración de Equipos
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Gestiona los equipos para filtrar el análisis de carga de trabajo
              </p>
            </div>
          </div>
          <Button onClick={openCreateDialog} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
            <Plus className="h-4 w-4" />
            Crear Equipo
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <div className="max-w-6xl mx-auto mb-6">
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">¿Qué es un equipo?</p>
                <p className="text-blue-700">
                  Un equipo es un grupo de usuarios de ClickUp cuyos datos quieres analizar juntos. 
                  Al crear un equipo, puedes filtrar el módulo de Análisis de Carga de Trabajo para ver 
                  únicamente las tareas asignadas a los miembros de ese equipo específico.
                </p>
                <p className="text-blue-700 mt-2">
                  <strong>Tip:</strong> Puedes asignar un "Manager" al equipo para que otro usuario 
                  también pueda ver y editar ese equipo.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teams List */}
      <div className="max-w-6xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Equipos Disponibles</CardTitle>
            <CardDescription>
              Equipos que has creado o donde tienes acceso
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teamsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
                <span className="ml-2 text-slate-500">Cargando equipos...</span>
              </div>
            ) : teams.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No hay equipos creados</p>
                <p className="text-sm mt-1">Crea tu primer equipo para comenzar a analizar datos</p>
                <Button onClick={openCreateDialog} className="mt-4 gap-2">
                  <Plus className="h-4 w-4" />
                  Crear mi primer equipo
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {teams.map(team => (
                  <Collapsible 
                    key={team.id}
                    open={expandedTeams[team.id]}
                    onOpenChange={() => toggleTeamExpanded(team.id)}
                  >
                    <div className="border rounded-lg hover:border-indigo-200 transition-colors">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-slate-800">{team.name}</h3>
                              <Badge variant="secondary" className="text-xs">
                                {team.members?.length || 0} miembros
                              </Badge>
                              {team.manager && (
                                <Badge variant="outline" className="text-xs gap-1">
                                  <UserCog className="h-3 w-3" />
                                  {team.manager.name || team.manager.email}
                                </Badge>
                              )}
                            </div>
                            {team.description && (
                              <p className="text-sm text-slate-500 mt-1">{team.description}</p>
                            )}
                            <p className="text-xs text-slate-400 mt-2">
                              Creado por: {team.created_by_email}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
                                {expandedTeams[team.id] ? (
                                  <>
                                    <ChevronUp className="h-4 w-4" />
                                    Ocultar
                                  </>
                                ) : (
                                  <>
                                    <ChevronDown className="h-4 w-4" />
                                    Ver miembros
                                  </>
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            {canEditTeam(team) && (
                              <>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => openEditDialog(team)}
                                        className="gap-1"
                                      >
                                        <Edit2 className="h-4 w-4" />
                                        Editar
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Editar equipo</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        variant="ghost" 
                                        size="icon"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setDeleteConfirm(team)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Eliminar equipo</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {/* Collapsible Members List */}
                      <CollapsibleContent>
                        <div className="border-t bg-slate-50/50 px-4 py-3">
                          <p className="text-xs font-medium text-slate-500 mb-2">Miembros del equipo:</p>
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                            {(team.members || []).map(m => (
                              <div 
                                key={m.user_id} 
                                className="flex items-center gap-2 bg-white rounded px-2 py-1.5 border text-sm"
                              >
                                <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-medium text-indigo-600">
                                  {m.username?.charAt(0)?.toUpperCase() || '?'}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-slate-700 truncate text-xs">{m.username}</p>
                                  <p className="text-slate-400 truncate text-[10px]">{m.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Team Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-indigo-600" />
              {editingTeam ? "Editar Equipo" : "Crear Nuevo Equipo"}
            </DialogTitle>
            <DialogDescription>
              Selecciona los miembros de ClickUp que formarán parte de este equipo
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            {/* Row 1: Team Name + Manager */}
            <div className="grid grid-cols-2 gap-4">
              {/* Team Name */}
              <div className="space-y-2">
                <Label htmlFor="teamName">Nombre del equipo *</Label>
                <Input
                  id="teamName"
                  placeholder="Ej: Desarrollo, SEO..."
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>
              
              {/* Manager Assignment */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <UserCog className="h-4 w-4" />
                  Manager (opcional)
                </Label>
                {systemUsersLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 h-10">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Cargando...
                  </div>
                ) : systemUsers.length > 0 ? (
                  <Select 
                    value={selectedManager?.user_id || "none"} 
                    onValueChange={handleManagerChange}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sin manager" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin manager</SelectItem>
                      {systemUsers.map(user => (
                        <SelectItem key={user.user_id} value={user.user_id}>
                          {user.name || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input disabled placeholder="Solo admins" className="bg-slate-50" />
                )}
              </div>
            </div>
            
            {/* Row 2: Description */}
            <div className="space-y-2">
              <Label htmlFor="teamDesc">Descripción (opcional)</Label>
              <Input
                id="teamDesc"
                placeholder="Breve descripción del equipo"
                value={teamDescription}
                onChange={(e) => setTeamDescription(e.target.value)}
              />
            </div>

            {/* Selected Members Count */}
            <div className="flex items-center justify-between">
              <Label>Miembros seleccionados</Label>
              <Badge variant={selectedMembers.length > 0 ? "default" : "secondary"}>
                {selectedMembers.length} seleccionados
              </Badge>
            </div>

            {/* User Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre o correo..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Users List */}
            <div className="border rounded-lg">
              {usersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
                  <span className="ml-2 text-sm text-slate-500">Cargando usuarios de ClickUp...</span>
                </div>
              ) : usersError ? (
                <div className="flex items-center justify-center py-8 text-red-500">
                  <AlertCircle className="h-5 w-5 mr-2" />
                  <span className="text-sm">{usersError}</span>
                </div>
              ) : (
                <ScrollArea className="h-[200px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12"></TableHead>
                        <TableHead>Usuario</TableHead>
                        <TableHead>Correo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-slate-500 py-8">
                            {searchQuery ? "No se encontraron usuarios" : "No hay usuarios disponibles"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map(user => {
                          const isSelected = selectedMembers.some(m => m.user_id === user.user_id);
                          return (
                            <TableRow 
                              key={user.user_id}
                              className={`cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}
                              onClick={() => handleMemberToggle(user)}
                            >
                              <TableCell>
                                <Checkbox 
                                  checked={isSelected}
                                  onCheckedChange={() => handleMemberToggle(user)}
                                />
                              </TableCell>
                              <TableCell className="font-medium">{user.username}</TableCell>
                              <TableCell className="text-slate-500">{user.email}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </div>

            {/* Selected Members Preview */}
            {selectedMembers.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-slate-600">Miembros seleccionados:</Label>
                <div className="flex flex-wrap gap-2">
                  {selectedMembers.map(m => (
                    <Badge 
                      key={m.user_id} 
                      variant="secondary"
                      className="gap-1 pr-1"
                    >
                      {m.username}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-4 w-4 p-0 hover:bg-transparent"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMemberToggle(m);
                        }}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSaveTeam}
              disabled={!teamName.trim() || selectedMembers.length === 0 || saving}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {editingTeam ? "Guardar cambios" : "Crear equipo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Eliminar equipo
            </DialogTitle>
            <DialogDescription>
              ¿Estás seguro de que deseas eliminar el equipo "{deleteConfirm?.name}"? 
              Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancelar
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleDeleteTeam(deleteConfirm?.id)}
              disabled={deleting}
              className="gap-2"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamConfigView;
